<template>
  <div ref="aceWrapper" class="ace-wrapper">
    <div id="ace-editor" class="ace-editor"></div>
  </div>
</template>

<script>
  import _ from 'underscore';

  import 'ace-builds/src-min-noconflict/ace';
  import 'ace-builds/src-min-noconflict/ext-language_tools';
  import 'ace-builds/src-min-noconflict/mode-markdown';
  import 'ace-builds/src-min-noconflict/mode-python';
  import 'ace-builds/src-min-noconflict/mode-php';
  import 'ace-builds/src-min-noconflict/mode-ruby';

  import '../ace_custom/mode-markddrive';
  import '../ace_custom/theme-markddrive';

  const TokenIterator = window.ace.require('ace/token_iterator').TokenIterator;
  const Range = window.ace.require('ace/range').Range;

  export default {
    name: 'ace',

    props: ['value'],

    data() {
      return {
        isReady: false,
      };
    },

    watch: {
      value() {
        this.init();
        this.isFirstChange = true;
        this.editor.getSession().off('slow-change');
        this.editor.setValue(this.value);
        this.editor.clearSelection();
        this.editor.gotoLine(0);
        // Reset undo manager to avoid first setValue.
        this.editor.getSession().setUndoManager(new window.ace.UndoManager());
        this.focus();
      },
    },

    methods: {
      createAceContainer() {
        const newAceEditorElement = document.createElement('div');
        newAceEditorElement.setAttribute('id', 'ace-editor');
        newAceEditorElement.classList.add('ace-editor');
        return newAceEditorElement;
      },

      destroy() {
        if (this.editor) {
          this.editor.destroy();
          while (this.$refs.aceWrapper.firstChild) {
            this.$refs.aceWrapper.removeChild(this.$refs.aceWrapper.firstChild);
          }
          this.editor = undefined;
          const newAceEditorElement = this.createAceContainer();
          this.$refs.aceWrapper.appendChild(newAceEditorElement);
        }
      },

      init() {
        /*eslint-disable */
        if (this.editor) {
          this.destroy();
        }
        this.isFirstChange = true;
        this.editor = window.ace.edit('ace-editor');
        this.editor.setTheme("theme/markddrive");
        this.editor.setBehavioursEnabled(true);
        this.editor.setShowPrintMargin(false);
        this.editor.setOptions({
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: false,
          scrollPastEnd: true
        });

        //disable message from ace.
        this.editor.$blockScrolling = Infinity;

        this.editor.on('mouseup', this.onMouseUp);

        const indent_list_regex = /^markup\.list/;

        this.editor.commands.addCommand({
          name: 'indent_list',
          bindKey: {win: "Tab", mac: "Tab"},
          exec: function(editor) {
            const selection = editor.session.getSelection();
            // If multiple lines are selected then call default indent behavior.
            if (selection.anchor.row !== selection.lead.row) { editor.indent(); }
            else {
              const pos = editor.getCursorPosition();
              const token = editor.session.getTokenAt(pos.row, pos.column);
              if (indent_list_regex.test(token.type)) {
                editor.gotoLine(pos.row + 1, 0);
                editor.indent();
                editor.gotoLine(pos.row + 1, pos.column + editor.session.getTabSize());
              }
              else { editor.indent(); }
            }
          },
          multiSelectAction: "forEach",
          scrollIntoView: "selectionPart"
        });
        this.editor.commands.addCommand({
          name: 'clean_list',
          bindKey: {win: "Ctrl+Shift+E", mac: "Cmd+Shift+E"},
          exec: function(editor) {
            let iterator = new TokenIterator(editor.session, 0, 0);
            let lists = this.buildListTree(iterator);
            lists.forEach((list) => {
              this.cleanNumberedList(editor, list);
            });
          }.bind(this),
        });

        this.session = this.editor.getSession();
        window._session = this.session;
        this.session.setMode("mode/markddrive");
        this.session.on('change', _.debounce(this.onDocumentChanged, 500));
        this.isReady = true;
        /*eslint-enable */
      },

      focus() {
        this.editor.focus();
      },

      onMouseUp(event, editor) {
        const position = editor.getCursorPosition();
        const token = editor.session.getTokenAt(position.row, position.column);
        console.log(token);
      },

      onDocumentChanged() {
        this.$emit('change', this.editor.getValue(), this.isFirstChange);
        const iterator = new TokenIterator(this.editor.session, 0, 0);
        const lists = this.buildListTree(iterator);
        lists.forEach((list) => {
          this.cleanNumberedList(this.editor, list);
        });
        if (this.isFirstChange) {
          this.isFirstChange = false;
          this.editor.session.on('change', _.debounce(this.onDocumentChanged5s, 5000));
        }
      },

      /**
       * Save document after 5 second without user interaction.
       */
      onDocumentChanged5s() {
        this.$emit('slow-change', this.editor.getValue());
      },

      buildList(iterator, list, _line) {
        let line = _line;
        while (iterator.stepForward()) {
          const currentToken = iterator.getCurrentToken();
          const match = /^markup\.list\.(numbered|unnumbered)\.level_(\d+)$/.exec(currentToken.type);
          if (match) {
            const item = {
              line: iterator.getCurrentTokenRow(),
              column: iterator.getCurrentTokenColumn(),
              type: match[1],
              level: parseInt(match[2], 10),
            };
            if (item.level === list.level) {
              list.items.push(item);
              line += 1;
            } else if (item.level === list.level + 1) {
              const sublist = {
                first: item.line,
                level: item.level,
                type: item.type,
                items: [item],
              };
              line = this.buildList(iterator, sublist, line);
              /*eslint-disable */
              list.items[list.items.length - 1].sublist = sublist;
              /*eslint-enable */
            } else {
              iterator.stepBackward();
              break;
            }
          }
        }
        return line + 1;
      },

      buildListTree(iterator) {
        const lists = [];
        let line = 0;
        let currentList = null;
        while (iterator.stepForward()) {
          const currentToken = iterator.getCurrentToken();
          const match = /^markup\.list\.(numbered|unnumbered)\.level_(\d+)$/.exec(currentToken.type);
          if (match) {
            const item = {
              line: iterator.getCurrentTokenRow(),
              column: iterator.getCurrentTokenColumn(),
              type: match[1],
              level: parseInt(match[2], 10),
            };
            if (item.line > line + 1) {
              // Push the current list to the list array.
              if (currentList) {
                lists.push(currentList);
              }
              // Create new list.
              currentList = {
                first: item.line,
                level: item.level,
                type: item.type,
                items: [item],
              };
              line = item.line;
            } else if (currentList && item.level === currentList.level + 1) {
              // Create sublist.
              const sublist = {
                first: item.line,
                level: item.level,
                type: item.type,
                items: [item],
              };
              line = this.buildList(iterator, sublist, line);
              currentList.items[currentList.items.length - 1].sublist = sublist;
            } else if (currentList && item.level === currentList.level) {
              currentList.items.push(item);
              line = item.line;
            }
          }
        }
        if (currentList) {
          lists.push(currentList);
        }
        return lists;
      },

      cleanNumberedList(editor, list) {
        /*eslint-disable */
        for (let i in list.items) {
          const item = list.items[i];
          if (list.type === 'numbered') {
            let line = editor.session.getLine(item.line);
            let index = parseInt(i) + 1;
            let new_line = line.replace(/\w*([0-9]+)\./, index + '.');
            if (new_line !== line) {
              const range = new Range(item.line, 0, item.line, line.length);
              editor.session.replace(range, new_line);
            }
          }
          if (item.sublist !== undefined) {
            this.cleanNumberedList(editor, item.sublist);
          }
        }
        /*eslint-enable */
      },
    },
  };
</script>

<style lang="scss">
  @import '../assets/colors';

  .ace-wrapper .ace-editor {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    font-size: 16px;
    background-color: lighten($black, 13%);
  }

  //urls
  $url_0: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAACCAYAAACZgbYnAAAAEklEQVQImWPQ0FD0ZXBzd/wPAAjVAoxeSgNeAAAAAElFTkSuQmCC);

  .markddrive.ace_dark {
    background-color: lighten($black, 13%);
    color: $white;

    .ace_gutter {
      background: lighten($black, 13%);
      color: darken($grey, 30%);
    }
    .ace_print-margin {
      width: 1px;
      background: $blue_grey;
    }
    .ace_cursor {
      color: $white;
    }
    .ace_marker-layer {
      .ace_selection {
        background: darken($grey, 44%);
      }
      .ace_step {
        background: $brown;
      }
      .ace_bracket {
        margin: -1px 0 0 -1px;
        border: 1px solid $brown;
      }
      .ace_active-line {
        background: darken($grey, 54%);
      }
      .ace_selected-word {
        border: 1px solid $brown;
      }
    }
    .ace_gutter-active-line {
      background-color: darken($grey, 54%);
    }
    .ace_invisible {
      color: $grey;
    }
    .ace_entity {
      &.ace_name {
        &.ace_tag {
          color: $red;
        }
        &.ace_function {
          color: $lime;
        }
      }
      &.ace_other {
        color: lighten($grey, 20%);
        &.ace_attribute-name {
          color: lighten($orange, 20%);
        }
      }
    }
    .ace_keyword {
      color: lighten($purple, 30%);

      &.ace_operator { color: lighten($light_blue, 30%); }
    }
    .ace_identifier { color: lighten($indigo, 20%); }
    .ace_storage {
      color: $red;

      &.ace_type {
        font-style: italic;
        color: $teal;
      }
    }
    .ace_punctuation {
      color: $white;

      &.ace_tag {
        color: $white;
      }
    }
    .ace_constant {
      &.ace_character {
        color: $purple;
      }
      &.ace_language {
        color: lighten($purple, 30%);
        &.ace_escape {
          color: $white;
        }
      }
      &.ace_numeric {
        color: lighten($indigo, 20%);
      }
      &.ace_other {
        color: $purple;
      }
    }
    .ace_invalid {
      color: $white;
      background-color: $red;

      &.ace_deprecated {
        color: $white;
        background-color: $purple;
      }
    }
    .ace_support {
      &.ace_constant {
        color: $teal;
      }
      &.ace_function {
        color: lighten($purple, 30%);
      }
      &.ace_class {
        font-style: italic;
        color: $teal;
      }
      &.ace_type {
        font-style: italic;
        color: $teal;
      }
      &.ace_variable {
        color: lighten($lime, 10%);
      }
      &.ace_php_tag {
        color: lighten($purple, 30%);
      }
    }
    .ace_fold {
      background-color: $lime;
      border-color: $white;
    }
    .ace_variable {
      color: $white;

      &.ace_parameter {
        font-style: italic;
        color: $orange;
      }
    }
    .ace_paren {
      color: lighten($light_blue, 30%);
    }
    .ace_string {
      color: lighten($lime, 10%);

      &.ace_blockquote {
        color: lighten($indigo, 20%);
      }
      &.ace_strong {
        color: lighten($red, 10%);
      }
      &.ace_attribute-value {
        color: lighten($green, 20%);
      }
      &.ace_emphasis {
        color: lighten($red, 20%);
      }
    }
    .ace_comment {
      color: $brown;
    }
    .ace_indent-guide {
      background: $url_0 right repeat-y;
    }
    &.ace_multiselect .ace_selection.ace_start {
      box-shadow: 0 0 3px 0 $blue_grey;
    }
    .ace_meta {
      &.ace_tag {
        color: lighten($light_blue, 30%);

        &.ace_tag-name {
          color: lighten($red, 10%);
        }
      }
    }
    .ace_heading {
      color: $indigo;

      &.ace_markup { font-weight: 800; }

      // &.ace_1, &.ace_1 + .ace_heading { color: $teal; }
      // &.ace_2, &.ace_2 + .ace_heading { color: $indigo; }
      // &.ace_3, &.ace_3 + .ace_heading { color: $purple; }
      // &.ace_4, &.ace_4 + .ace_heading { color: $pink; }
      // &.ace_5, &.ace_5 + .ace_heading { color: $red; }
      // &.ace_6, &.ace_6 + .ace_heading { color: $orange; }
    }
    .ace_markup {
      &.ace_list {
        &.ace_numbered {
          &.ace_level_0 { color: lighten($red, 10%); }
          &.ace_level_1 { color: lighten($pink, 10%); }
          &.ace_level_2 { color: lighten($purple, 10%); }
          &.ace_level_3 { color: lighten($red, 20%); }
          &.ace_level_4 { color: lighten($pink, 20%); }
          &.ace_level_5 { color: lighten($purple, 20%); }
        }
        &.ace_unnumbered {
          position: relative;
          color: transparent;
          &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 25%;
            width: 32%;
            height: 32%;
            transform: translate(-50%, -50%);
            border-radius: 50%;
            background: $white;
          }
          &.ace_level_0::after { background: lighten($blue, 10%); }
          &.ace_level_1::after { background: lighten($indigo, 10%); }
          &.ace_level_2::after { background: lighten($deep_purple, 10%); }
          &.ace_level_3::after { background: lighten($blue, 20%); }
          &.ace_level_4::after { background: lighten($indigo, 20%); }
          &.ace_level_5::after { background: lighten($deep_purple, 20%); }
        }
      }
      &.ace_underline { color: $grey; }
    }
    .ace_list {
      color: lighten($grey, 20%);
    }
    .ace_xml-pe {
      &.ace_xml { color: lighten($orange, 20%); }
      &.ace_doctype.ace_xml { color: lighten($light_blue, 30%); }
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/Ace.vue?05f5a43f